/**
 * Tarifs v2 — fonctions pures (docs/ux-v2/SPEC.md § 4.6, TA1, TA2, TA4).
 *
 * La page v2 rend la MÊME configuration que la page Tarifs administrable de la v1
 * (screens/Prices/components/ServicesContracts.tsx, réglages du BO « Tarifs ») : mêmes blocs, même
 * ordre, mêmes règles de segments et de sous-onglets, mêmes textes (fusion config + Mindbody par
 * screens/Prices/utils/mergePricingService.ts, importé tel quel). Ici, seulement ce que la v2 ajoute :
 * la typographie des montants, les couleurs lisibles sur les bandeaux du BO, le choix des colonnes.
 *
 * Aucun effet de bord au chargement du module.
 */
import type { PricingCategory, PricingConfig, PricingContract, PricingSubTab } from 'types/pricing';
import { hslOf, hueDistance, normalizeHex, textOnColor, WHITE } from '../../lib/color';

/**
 * Espace insécable avant € et entre les milliers. Intl fr-FR met une espace fine insécable (U+202F) entre les milliers,
 * mais la police du thème ne l'a pas : WebKit la rend sur 0,6 px (« 1450 ») — revue du 05/10.
 */
const NBSP = ' ';
const NNBSP = ' ';

/** « 1450 » → « 1 450 » (espace insécable). */
function groupThousands(integer: string): string {
  return integer.replace(/\B(?=(\d{3})+(?!\d))/g, NNBSP);
}

/**
 * Typographie française des montants dans un libellé déjà rendu par les gabarits du BO
 * (mergePricingService : « 15€ », « 18,9€ / séance », « soit 949€ le pack ») :
 * « 15 € », « 18,90 € / séance », « soit 949 € le pack ». Le texte autour n'est pas touché ;
 * les montants sans « € » non plus. La valeur ne change jamais (18,9 = 18,90) : seule l'écriture
 * suit la maquette (« 18,90 € la séance »).
 * `padDecimals: false` : décimales telles que calculées (« 18,9 € » par séance : règle de Daphné du
 * 19/10, une décimale arrondie en dessous, rappelée le 21/10 : « tu peux en laisser qu'une seule »).
 */
export function frenchMoney(
  label: string | null | undefined,
  { padDecimals = true }: { padDecimals?: boolean } = {},
): string {
  if (!label) return '';
  return label.replace(/(\d+)(?:[.,](\d+))?\s*€/g, (_m, integer: string, decimals: string | undefined) => {
    const int = groupThousands(integer);
    if (decimals === undefined) return `${int}${NBSP}€`;
    const dec = padDecimals && decimals.length === 1 ? `${decimals}0` : decimals;
    return `${int},${dec}${NBSP}€`;
  });
}

/** Tri par `sortOrder` (copie : la config injectée n'est jamais modifiée). */
export function bySortOrder<T extends { sortOrder: number }>(items: readonly T[]): T[] {
  return [...items].sort((a, b) => a.sortOrder - b.sortOrder);
}

/** Couleur du site déjà posée sur la page (marque, action) : la couleur, sa surface sous un texte, ce texte. */
export interface SiteColor {
  color: string;
  surface: string;
  on: string;
}

/**
 * Couleur du BO qui est, à l'œil, celle du site (même teinte à 8° près, saturation et luminosité voisines) : on
 * reprend la surface du site plutôt qu'une nouvelle teinte foncée au minimum. Revue du 05/10 : ruban #FF2021 foncé
 * en #E91D1E à côté des boutons #E0141C, deux rouges dans une même carte (ΔE ≈ 3).
 */
export function siteColorFor(color: string, palette: readonly SiteColor[] = []): SiteColor | null {
  const a = hslOf(color);
  if (!a) return null;
  return (
    palette.find((site) => {
      const b = hslOf(site.color);
      return (
        !!b &&
        hueDistance(color, site.color) <= 8 &&
        Math.abs(a.s - b.s) <= 0.25 &&
        Math.abs(a.l - b.l) <= 0.15
      );
    }) ?? null
  );
}

/**
 * Couleurs d'un bandeau (ruban) réglé dans le BO : fond = couleur du ruban, sinon la couleur
 * d'accent de l'offre, sinon le repli (encre, comme la maquette) ; texte selon la convention des
 * marques (lib/color.ts textOnColor : blanc sur une couleur moyenne ou foncée, fond foncé au minimum
 * pour 4,5:1 ; encre sur une couleur claire).
 */
export function bandColors(
  ribbonColor: string | null | undefined,
  accentColor: string | null | undefined,
  fallback: string,
  ink: string,
  palette: readonly SiteColor[] = [],
): { background: string; color: string } {
  const setting = normalizeHex(ribbonColor) ?? normalizeHex(accentColor);
  const site = setting ? siteColorFor(setting, palette) : null;
  if (site) return { background: site.surface, color: site.on };
  const { surface, text } = textOnColor(setting ?? fallback, ink, WHITE);
  return { background: surface, color: text };
}

/** Fond et texte lisibles pour une couleur de fond réglée dans le BO (même règle ; null si illisible). */
export function readableOnSetting(
  color: string | null | undefined,
  ink: string,
  palette: readonly SiteColor[] = [],
): { background: string; color: string } | null {
  const background = normalizeHex(color);
  if (!background) return null;
  const site = siteColorFor(background, palette);
  if (site) return { background: site.surface, color: site.on };
  const { surface, text } = textOnColor(background, ink, WHITE);
  return { background: surface, color: text };
}

export type SegmentId = 'packs' | 'abonnements';

export interface SegmentOption {
  id: SegmentId;
  label: string;
  sortOrder: number;
}

/**
 * Segments PACKS / ABONNEMENTS, mêmes règles que ServicesContracts.tsx (NavigationLayout) :
 * - segment initial : PACKS s'il y a des catégories, sinon ABONNEMENTS ;
 * - sélecteur affiché seulement si les deux segments ont du contenu ;
 * - libellés du BO (repli « PACKS » / « ABONNEMENTS »), ordre `sortOrder` (repli 0 / 1).
 */
export function resolveSegments(config: Pick<PricingConfig, 'segments' | 'categories' | 'contracts'>): {
  options: SegmentOption[];
  initial: SegmentId;
  showSelector: boolean;
} {
  const packsHasItems = config.categories.length > 0;
  const abosHasItems = config.contracts.length > 0;
  const segments = config.segments;
  const options: SegmentOption[] = [
    { id: 'packs' as const, label: segments?.packs?.label || 'PACKS', sortOrder: segments?.packs?.sortOrder ?? 0 },
    {
      id: 'abonnements' as const,
      label: segments?.abonnements?.label || 'ABONNEMENTS',
      sortOrder: segments?.abonnements?.sortOrder ?? 1,
    },
  ].sort((a, b) => a.sortOrder - b.sortOrder);
  return {
    options,
    initial: packsHasItems ? 'packs' : 'abonnements',
    showSelector: packsHasItems && abosHasItems,
  };
}

/**
 * Sous-onglet actif (= SegmentBody de ServicesContracts.tsx) : celui choisi s'il existe encore,
 * sinon le premier ; null quand le segment n'a pas de sous-onglets (tout est affiché).
 */
export function activeSubTab(ordered: PricingSubTab[], activeId: string | null): PricingSubTab | null {
  return ordered.find((s) => s.id === activeId) ?? ordered[0] ?? null;
}

/** Catégories visibles sous un sous-onglet (toutes s'il n'y en a pas). */
export function visibleCategories(ordered: PricingCategory[], subTab: PricingSubTab | null): PricingCategory[] {
  return subTab ? ordered.filter((cat) => subTab.categoryIds?.includes(cat.id)) : ordered;
}

/** Abonnements visibles sous un sous-onglet (tous s'il n'y en a pas). */
export function visibleContracts(contracts: PricingContract[], subTab: PricingSubTab | null): PricingContract[] {
  return subTab ? contracts.filter((c) => subTab.contractIds?.includes(c.id)) : contracts;
}

/**
 * Colonnes d'une grille de cartes sur grand écran : autant que de cartes, au plus `max`
 * (réglage « colonnes » des abonnements, 3 par défaut comme la maquette `.packgrid`).
 */
export function gridColumns(count: number, max = 3): number {
  const cap = Math.max(1, Math.min(4, Math.round(max) || 3));
  return Math.max(1, Math.min(count, cap));
}

/**
 * Grille en demi-colonnes (chaque carte en couvre 2) : colonne de départ (base 1) de la carte `index` sur `count` dans
 * une grille de `cols` cartes de front, pour que les cartes de la dernière rangée incomplète soient centrées (= la v1,
 * TopBlocksRow en flex centré). « auto » pour toutes les autres. Revue du 05/10 : 5e bloc « top » de Dynamo Paris seul,
 * calé à gauche, à côté de 3 colonnes vides.
 */
export function orphanStart(index: number, count: number, cols: number): string {
  const rest = count % cols;
  if (cols <= 1 || rest === 0 || index !== count - rest) return 'auto';
  return String(cols - rest + 1);
}

/** La mention Alma d'une carte cite-t-elle déjà Alma ? (sinon la v2 ajoute le mot, à la place du logo v1). */
export function mentionsAlma(text: string | null | undefined): boolean {
  return /alma/i.test(text ?? '');
}

/** Texte non vide après nettoyage (le BO enregistre parfois « » pour « pas de titre »). */
export function hasText(text: string | null | undefined): text is string {
  return typeof text === 'string' && text.trim() !== '';
}

/** Cours transmis par la fiche (« Acheter des crédits ») : `navigate('/tarifs', { state: { classId } })`. */
export function classIdFromState(state: unknown): string | undefined {
  const classId = state && typeof state === 'object' ? (state as { classId?: unknown }).classId : undefined;
  if (typeof classId === 'number' && Number.isFinite(classId)) return String(classId);
  return typeof classId === 'string' && /^\d+$/.test(classId) ? classId : undefined;
}

/**
 * CGV du BO sans leurs paragraphes vides (« <p>&nbsp;</p> », 7 sur 14 sur Dynamo : grands blancs dans la modale, revue
 * du 04/10). Le reste du HTML est rendu tel quel, comme la v1.
 */
export function withoutEmptyParagraphs(html: string): string {
  // Paragraphe vide fermé (« <p>&nbsp;</p> ») ou laissé ouvert jusqu'au suivant (« <p><br>\n\n<p>… », HTML du BO).
  return html.replace(/<p\b[^>]*>(?:\s|&nbsp;|&#160;|\u00a0|<br\s*\/?>)*(?:<\/p>|(?=<p\b)|$)/gi, '');
}
